class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.grid = this.querySelector('[data-cart-upsell-grid]');
    this.limit = parseInt(this.dataset.limit || '4', 10);
    this.currency = this.dataset.currency || 'PLN';
    this.intent = this.dataset.intent || 'related';
    this.sliderId = this.dataset.sliderId || '';
    this.slider = this.sliderId ? this.querySelector(`#${CSS.escape(this.sliderId)}`) : null;
    this.carousel = this.querySelector('carousel-slider');
    this.prevBtn = this.querySelector('[name="prev"]');
    this.nextBtn = this.querySelector('[name="next"]');
    this.bindNav();
    this.init();
  }

  bindNav() {
    if (!this.slider) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
    }

    let scrollTimer;
    this.slider.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => this.updateNavState(), 100);
    }, { passive: true });

    window.addEventListener('resize', () => this.updateNavState());

    if (this.prevBtn) this.prevBtn.disabled = true;
    if (this.nextBtn) this.nextBtn.disabled = true;
  }

  scrollByPage(direction) {
    if (!this.slider) return;
    this.slider.scrollBy({ left: this.slider.clientWidth * direction, behavior: 'smooth' });
  }

  updateNavState() {
    if (!this.slider || !this.prevBtn || !this.nextBtn) return;

    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth - 1;
    const hasOverflow = this.slider.scrollWidth > this.slider.clientWidth + 1;

    if (this.carousel) {
      if (hasOverflow) {
        this.carousel.removeAttribute('inactive');
      } else {
        this.carousel.setAttribute('inactive', '');
      }
    }

    if (!hasOverflow) {
      this.prevBtn.disabled = true;
      this.nextBtn.disabled = true;
      return;
    }

    this.prevBtn.disabled = this.slider.scrollLeft <= 0;
    this.nextBtn.disabled = this.slider.scrollLeft >= maxScroll;
  }

  async init() {
    let cart;
    try {
      cart = await fetch('/cart.js').then(r => r.json());
    } catch (e) {
      console.warn('[cart-upsell] błąd pobierania koszyka:', e);
      return;
    }

    if (!cart.items.length) {
      return;
    }

    const cartProductIds = new Set(cart.items.map(i => i.product_id));
    const manualProducts = this.getManualProducts();
    let candidates;

    if (manualProducts.length > 0) {

      candidates = manualProducts;
    } else {
      const requests = [...cartProductIds].map(id => this.fetchRecommendationsWithFallback(id));
      const results = await Promise.all(requests);

      candidates = [];
      const maxLen = Math.max(0, ...results.map(r => r.length));
      for (let i = 0; i < maxLen; i++) {
        for (const group of results) {
          if (group[i]) candidates.push(group[i]);
        }
      }
    }

    const seen = new Set();
    const products = candidates.filter(p => {
      if (cartProductIds.has(p.id) || seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    }).slice(0, this.limit);

    if (!products.length) {
      this.classList.add('cart-upsell--hidden');
      return;
    }

    this.render(products);
    this.grid.hidden = false;

    requestAnimationFrame(() => this.updateNavState());
  }

  getManualProducts() {
    const script = this.querySelector('[data-cart-upsell-manual-products]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent) || [];
    } catch (e) {
      console.warn('[cart-upsell] błąd parsowania ręcznie wybranych produktów:', e);
      return [];
    }
  }

  async fetchRecommendationsWithFallback(productId) {
    let products = await this.fetchRecommendations(productId, this.intent);

    const fallbackIntent = this.intent === 'related' ? 'complementary' : 'related';
    if (!products.length) {
      products = await this.fetchRecommendations(productId, fallbackIntent);
    }

    return products;
  }

  fetchRecommendations(productId, intentType) {
    return fetch(`/recommendations/products.json?product_id=${productId}&intent=${intentType}&limit=${this.limit}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status} dla product_id=${productId}`);
        return r.json();
      })
      .then(data => data.products || [])
      .catch(err => {
        console.warn('[cart-upsell] błąd pobierania rekomendacji:', err);
        return [];
      });
  }

  render(products) {
    this.classList.remove('cart-upsell--hidden');
    this.grid.innerHTML = products.map((p, index) => `
      <div class="slider__item${index === 0 ? ' is-active' : ''}">
        <div class="block-inner-inner">
          <div class="image-cont">
            <a class="product-link" href="${p.url}" aria-hidden="true" tabindex="-1">
              <div class="image-label-wrap">
                <div class="img-ar img-ar--contain" style="--aspect-ratio: 1.0">
                  <img
                    src="${p.featured_image?.url || p.images?.[0] || ''}"
                    alt="${p.title}"
                    width="1000"
                    height="1000"
                    loading="lazy"
                  >
                </div>
              </div>
            </a>
          </div>

          <div class="product-block__detail">
            <div class="inner">
              <div class="innerer">
                <a class="product-link" href="${p.url}">
                  <div class="vendor">${p.vendor}</div>
                  <div class="product-block__title">${p.title}</div>
                </a>
                <div class="product-block__price">${this.formatMoney(p.price)}</div>
                <button
                  type="button"
                  class="cart-upsell__add"
                  data-variant-id="${p.variants[0].id}"
                >Add to cart</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `).join('');

    this.grid.querySelectorAll('[data-variant-id]').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn));
    });
  }

  async addToCart(btn) {
    btn.disabled = true;
    btn.textContent = 'Adding to the cart...';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: btn.dataset.variantId, quantity: 1 })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      btn.classList.add('is-added');
      btn.textContent = 'Added to the cart';
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      console.warn('[cart-upsell] fail while adding to cart:', e);
      btn.disabled = false;
      btn.textContent = 'Error, try again';
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('pl-PL', { style: 'currency', currency: this.currency });
  }
}

customElements.define('cart-upsell', CartUpsell);
